import config from "../config/config";
import { getToken, getUserData } from "../common";

const TRACKING_SESSION_ID_KEY = "BidWebTrackingSessionId";
const ACTIVITY_QUEUE_LIMIT = 10;
const ACTIVITY_FLUSH_INTERVAL_MS = 3000;

let activityQueue = [];
let flushTimer = null;

const scheduleFlush = () => {
  if (flushTimer) {
    return;
  }

  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushActivityQueue();
  }, ACTIVITY_FLUSH_INTERVAL_MS);
};

export const getTrackingSessionId = () => {
  const existing = sessionStorage.getItem(TRACKING_SESSION_ID_KEY);
  if (existing) {
    return existing;
  }

  const generated =
    window.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  sessionStorage.setItem(TRACKING_SESSION_ID_KEY, generated);
  return generated;
};

const buildHeaders = () => {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    "x-session-id": getTrackingSessionId(),
  };

  if (token) {
    headers.Authorization = token;
  }

  return headers;
};

const normalizeText = (value) => {
  if (!value) {
    return "";
  }

  return value.replace(/\s+/g, " ").trim().slice(0, 200);
};

export const createActivityEvent = (payload) => {
  const userData = getUserData() ?? {};

  return {
    eventType: payload.eventType,
    actionName: payload.actionName || "",
    entityType: payload.entityType || null,
    entityId: payload.entityId || null,
    page: window.location.pathname,
    route: window.location.hash || window.location.pathname,
    role: userData.role || null,
    targetUserId: payload.targetUserId || null,
    targetRole: payload.targetRole || null,
    sessionId: getTrackingSessionId(),
    timestamp: new Date().toISOString(),
    outcome: payload.outcome || "SUCCESS",
    metadata: {
      ...payload.metadata,
      impersonatedBy: userData.impersonatedBy || null,
    },
  };
};

export const flushActivityQueue = async ({ useBeacon = false } = {}) => {
  if (activityQueue.length === 0) {
    return;
  }

  const queuedEvents = [...activityQueue];
  activityQueue = [];

  const endpoint = `${config.api.base_url}activity-logs`;
  const body = JSON.stringify({ events: queuedEvents });

  if (useBeacon && navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon(endpoint, blob);
    return;
  }

  try {
    await fetch(endpoint, {
      method: "POST",
      headers: buildHeaders(),
      body,
      keepalive: true,
    });
  } catch (error) {
    activityQueue = [...queuedEvents, ...activityQueue].slice(-50);
  }
};

export const queueActivityEvent = (payload) => {
  if (!payload?.eventType) {
    return;
  }

  activityQueue.push(createActivityEvent(payload));
  activityQueue = activityQueue.slice(-50);

  if (activityQueue.length >= ACTIVITY_QUEUE_LIMIT) {
    flushActivityQueue();
    return;
  }

  scheduleFlush();
};

export const trackPageVisit = ({ route, pageTitle }) => {
  queueActivityEvent({
    eventType: "PAGE_VISIT",
    actionName: "Page visit",
    metadata: {
      route,
      pageTitle,
      referrer: document.referrer || null,
    },
  });
};

export const trackButtonClick = (target) => {
  if (!target) {
    return;
  }

  const button = target.closest("button, a, [role='button'], input[type='submit']");
  if (!button) {
    return;
  }

  const actionName =
    normalizeText(button.innerText) ||
    button.getAttribute("aria-label") ||
    button.getAttribute("title") ||
    button.getAttribute("name") ||
    "Button click";

  queueActivityEvent({
    eventType: "BUTTON_CLICK",
    actionName,
    entityType: button.tagName.toLowerCase(),
    entityId: button.id || button.name || null,
    metadata: {
      href: button.getAttribute("href") || null,
      className: button.className || null,
    },
  });
};

export const trackFormSubmit = (form) => {
  if (!form) {
    return;
  }

  queueActivityEvent({
    eventType: "FORM_SUBMIT",
    actionName: form.getAttribute("name") || form.id || "Form submit",
    entityType: "form",
    entityId: form.id || form.getAttribute("name") || null,
    metadata: {
      action: form.getAttribute("action") || null,
      method: form.getAttribute("method") || "GET",
    },
  });
};

export const trackBusinessAction = ({
  role,
  actionName,
  entityType,
  entityId,
  outcome,
  metadata,
}) => {
  queueActivityEvent({
    eventType: role === "SELLER" ? "SELLER_ACTION" : "BUYER_ACTION",
    actionName,
    entityType,
    entityId,
    outcome,
    metadata,
  });
};

export const trackLogoutEvent = () => {
  queueActivityEvent({
    eventType: "LOGOUT",
    actionName: "User logout",
  });
  flushActivityQueue({ useBeacon: true });
};
